import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Inbox from "./components/Inbox";
import { CommunicationService } from "@propertelligent/client-api";
import Compose from "./components/Compose";
import ShowEmail from "./components/ShowEmail";
import ShowInboxMessage from "./components/ShowInboxMessage";
import ShowOutlookMessage from "./components/ShowOutlookMessage";
import AddGmailAccount from "./components/AddGmailAccount";
import EmptyState from "../../../newComponents/emptystate";
import EmailListingTopHeader from "../../../newComponents/listings/top-listing-headers/email-listing-top-header";
import SimpleSpinner from "../../loader";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import { GetImages } from "../../../utils/GetImages";
import { Background } from "../../GlobalCss/Background";
import { usePermissions } from "../../../hooks/usePermissions";
import { getProviderStatus, listInboxMessages, listOutlookMessages, getOutlookEmailCounts, formatLocalDisplayDate, getOutlookProfile, EmailProviderType } from "./personalEmailApi";
import { resolveSmtpConnectedEmail, filterThreadsBySearch, mapOutlookMessages } from "./emailViewHelpers";

const PAGE_SIZE = 10;
// POP3 has no pagination — fetch a larger flat window than a single Gmail page so a stateless list still feels
// reasonably populated (see the "Deliberately stateless" note on PersonalSmtpProvider for why there's no cache).
const INBOX_FETCH_SIZE = PAGE_SIZE * 3;

const Emailview = () => {
  const [activeIndex, setActiveIndex] = useState("Inbox");
  const [providerType, setProviderType] = useState<EmailProviderType>(EmailProviderType.None);
  const [emaillist, setEmailList] = useState([]);
  const [threadlist, setThreadList] = useState([]);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined);
  const [listLoading, setListLoading] = useState(false);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);
  const [emailId, setEmailId] = useState<any>();
  const [searchEmail, setSearchEmail] = useState("");
  const [emailCat, setEmailCat] = useState("all");
  const [compose, setcompose] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState("");
  const [emailCounts, setEmailCounts] = useState({
    inbox: 0,
    important: 0,
    sent: 0,
    starred: 0,
  });

  const { canWrite } = usePermissions();
  const hasWritePermission = canWrite("Email");

  const isGmailConnected = providerType === EmailProviderType.Gmail;
  const isSmtpConnected = providerType === EmailProviderType.CustomPopSmtp;
  const isOutlookConnected = providerType === EmailProviderType.Office365;

  const queryString = `in:${activeIndex.toLowerCase()} subject:${searchEmail.toLowerCase()} is:${emailCat.toLowerCase()}`;

  const loadProviderStatus = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    getProviderStatus()
      .then((status) => {
        setProviderType(status.providerType);
        // Gmail's provider-status response deliberately never carries the real address (GmailController.cs's
        // GetProviderStatus comment: that requires a live Gmail API call, which risks disconnecting the account
        // on a transient error) -- fetch it separately via fetchGmailConnectedEmail below, keyed off isGmailConnected.
        setConnectedEmail(resolveSmtpConnectedEmail(status));
      })
      .catch((error) => {
        // Distinct from "nothing connected" -- the request itself failed (network/5xx), so don't show the
        // connect screen (which would look like the user's account got silently disconnected).
        console.error("Error loading email provider status:", error);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  // Gmail-only, deliberately separate from loadProviderStatus (see the comment above): resolves the real "connected
  // as <address>" value off the profile endpoint. A failure here just leaves connectedEmail blank in the disconnect
  // dialog -- it never touches the connection itself.
  const fetchGmailConnectedEmail = useCallback(() => {
    CommunicationService.getApiGmailProfile()
      .then((res: any) => {
        setConnectedEmail(res?.success?.emailAddress ?? "");
      })
      .catch((error) => {
        console.error("Error loading Gmail profile:", error);
      });
  }, []);

  const onGetEmailList = useCallback((pageToken?: string, append = false) => {
    if (!isGmailConnected) return;
    setListLoading(true);
    CommunicationService.getApiGmailListEmailThreads(
      pageToken ?? undefined,
      PAGE_SIZE,
      queryString
    )
      .then((res: any) => {
        const data = res?.success ?? res;
        const threads = data?.emailThreads ?? data?.threads ?? [];
        const nextToken = data?.nextPageToken ?? data?.nextPageToken ?? undefined;
        setThreadList((prev) => (append ? [...prev, ...threads] : threads));
        setNextPageToken(nextToken);
        setHasMoreThreads(!!nextToken);
        setListLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching threads:", error);
        setListLoading(false);
        setHasMoreThreads(false);
      });
  }, [isGmailConnected, queryString]);

  // POP3 has no pagination token, no search/filter server-side — a flat maxResults fetch, most-recent-first.
  // Guarded against overlapping calls: most POP3 servers hold an exclusive mailbox lock per session, so firing a
  // second fetch before the first's DisconnectAsync completes causes "-ERR mailbox in use" / a race on the list.
  const inboxFetchInFlightRef = useRef(false);
  const onGetInboxMessages = useCallback(() => {
    if (!isSmtpConnected || inboxFetchInFlightRef.current) return;
    inboxFetchInFlightRef.current = true;
    setListLoading(true);
    listInboxMessages(INBOX_FETCH_SIZE)
      .then((messages) => {
        const mapped = messages.map((m) => ({
          id: m.uid,
          fromName: m.fromName ?? m.fromEmail ?? m.from ?? "",
          subject: m.subject,
          displayDate: formatLocalDisplayDate(m.date),
          date: m.date,
        }));
        setThreadList(mapped as any);
        setHasMoreThreads(false);
      })
      .catch((error) => {
        console.error("Error fetching inbox messages:", error);
        setHasMoreThreads(false);
      })
      .finally(() => {
        setListLoading(false);
        inboxFetchInFlightRef.current = false;
      });
  }, [isSmtpConnected]);

  // Outlook-only, deliberately separate from loadProviderStatus (same reasoning as
  // fetchGmailConnectedEmail above): the personal provider-status branch never populates EmailAddress
  // for Outlook, so resolve the real "connected as <address>" value off the profile endpoint. A failure
  // here just leaves connectedEmail blank in the disconnect dialog — it never touches the connection.
  const fetchOutlookConnectedEmail = useCallback(() => {
    getOutlookProfile()
      .then((address) => setConnectedEmail(address ?? ""))
      .catch((error) => {
        console.error("Error loading Outlook profile:", error);
      });
  }, []);

  // Outlook: hybrid of Gmail's shape (real server paging -- Graph supports it, unlike POP3) and SMTP's flat-mapped
  // threadlist shape (no thread grouping -- Graph has no thread-equivalent resource). Shares the same
  // nextPageToken/hasMoreThreads state Gmail uses above since only one provider is ever connected at a time.
  const onGetOutlookMessages = useCallback((pageToken?: string, append = false) => {
    if (!isOutlookConnected) return;
    setListLoading(true);
    listOutlookMessages(PAGE_SIZE, searchEmail, pageToken, activeIndex)
      .then(({ messages, nextPageToken: nextToken }) => {
        const mapped = mapOutlookMessages(messages);
        setThreadList((prev) => (append ? [...(prev as any[]), ...mapped] : mapped) as any);
        setNextPageToken(nextToken ?? undefined);
        setHasMoreThreads(!!nextToken);
      })
      .catch((error) => {
        console.error("Error fetching Outlook messages:", error);
        setHasMoreThreads(false);
      })
      .finally(() => setListLoading(false));
  }, [isOutlookConnected, searchEmail, activeIndex]);

  const onGetOutlookMessageCounts = useCallback(() => {
    if (!isOutlookConnected) return;
    getOutlookEmailCounts()
      .then((counts) => {
        setEmailCounts((prev) => ({ ...prev, inbox: counts.inbox, sent: counts.sent, starred: counts.starred }));
      })
      .catch((error) => {
        console.error("Error fetching Outlook email counts:", error);
      });
  }, [isOutlookConnected]);

  const loadMoreEmailThreads = useCallback(() => {
    if (listLoading || !hasMoreThreads) return;
    if (nextPageToken !== undefined && nextPageToken !== null && nextPageToken !== "") {
      if (isOutlookConnected) {
        onGetOutlookMessages(nextPageToken, true);
      } else {
        onGetEmailList(nextPageToken, true);
      }
    }
  }, [listLoading, hasMoreThreads, nextPageToken, isOutlookConnected, onGetEmailList, onGetOutlookMessages]);

  const onGetEmailCount = useCallback(() => {
    if (!isGmailConnected) return;
    CommunicationService.getApiGmailEmailCounts()
      .then((res) => {
        setEmailCounts((prev) => ({
          ...prev,
          inbox: res.success.inbox,
          important: res.success.important,
          sent: res.success.sent,
        }));
      })
      .catch((error) => {
        console.error(error);
      });
  }, [isGmailConnected]);

  // Initial load: which provider (if any) is connected.
  useEffect(() => {
    loadProviderStatus();
  }, [loadProviderStatus]);

  // Gmail: refetch the thread list on any search/filter change (the query is server-side via queryString).
  useEffect(() => {
    if (!isGmailConnected) return;
    onGetEmailList(undefined, false);
  }, [isGmailConnected, searchEmail, activeIndex, emailCat, onGetEmailList]);

  // Gmail: counts only depend on which mailbox is connected, not on search/tab/filter — keeping this in a
  // separate effect stops it refetching on every search keystroke.
  useEffect(() => {
    if (!isGmailConnected) return;
    onGetEmailCount();
  }, [isGmailConnected, onGetEmailCount]);

  // Gmail: resolve the real connected address separately from provider-status (see fetchGmailConnectedEmail).
  useEffect(() => {
    if (!isGmailConnected) return;
    fetchGmailConnectedEmail();
  }, [isGmailConnected, fetchGmailConnectedEmail]);

  // Outlook: resolve the real connected address separately from provider-status (see
  // fetchOutlookConnectedEmail).
  useEffect(() => {
    if (!isOutlookConnected) return;
    fetchOutlookConnectedEmail();
  }, [isOutlookConnected, fetchOutlookConnectedEmail]);

  // Outlook: refetch the message list on any search/filter change (server-side query via Graph $search),
  // mirroring the Gmail effect above.
  useEffect(() => {
    if (!isOutlookConnected) return;
    onGetOutlookMessages(undefined, false);
  }, [isOutlookConnected, searchEmail, activeIndex, emailCat, onGetOutlookMessages]);

  // Outlook: counts only depend on which mailbox is connected, not on search/tab/filter -- same reasoning as
  // the Gmail counts effect above.
  useEffect(() => {
    if (!isOutlookConnected) return;
    onGetOutlookMessageCounts();
  }, [isOutlookConnected, onGetOutlookMessageCounts]);

  // POP3 has no server-side search/filter/label concept — only refetch when the SMTP connection itself changes,
  // never on searchEmail/activeIndex/emailCat (those inputs are meaningless for this branch and, since the search
  // box is un-debounced, including them here would fire a fresh POP3 session on every keystroke).
  useEffect(() => {
    if (!isSmtpConnected) return;
    onGetInboxMessages();
  }, [isSmtpConnected, onGetInboxMessages]);

  // POP3 has no server-side search — filter the already-fetched window client-side instead of hiding the (shared)
  // search box, so the SMTP branch doesn't need its own EmailListingTopHeader variant.
  const displayedThreadlist = useMemo(
    () => (isSmtpConnected ? filterThreadsBySearch(threadlist as any[], searchEmail) : threadlist),
    [threadlist, isSmtpConnected, searchEmail]
  );

  const filterOptions = isGmailConnected
    ? [
        { title: "Inbox", count: emailCounts?.inbox },
        { title: "Sent", count: emailCounts?.sent },
        { title: "Important", count: emailCounts?.important },
      ]
    : isOutlookConnected
    ? [
        { title: "Inbox", count: emailCounts?.inbox },
        { title: "Sent", count: emailCounts?.sent },
        { title: "Starred", count: emailCounts?.starred },
      ]
    : [{ title: "Inbox", count: displayedThreadlist?.length ?? 0 }];

  // Read/Unread counts aren't wired up server-side for either provider (stub 0/0). For SMTP specifically, every
  // message returned by GetInboxMessagesAsync is unread by construction -- POP3 (Gmail's included) stops offering
  // a message the moment it's marked read anywhere, so there is no meaningful Read/Unread split to compute.
  const readUnreadFilters = { Read: 0, Unread: 0 };

  const handleSelectChange = useCallback((value: string) => setEmailCat(value), []);

  const handleDisconnect = () => {
    setLoading(true);
    // Provider-agnostic on the backend: deletes whichever row is connected (Gmail or SMTP).
    CommunicationService.deleteApiGmailDeleteGmailSettings()
      .then((res) => {
        if (res.success) {
          setProviderType(EmailProviderType.None);
          setEmailList([]);
          setThreadList([]);
          setConnectedEmail("");
          // Clear paging/selection state too -- a Gmail page token or Outlook message id left over from the
          // disconnected provider would otherwise survive into whatever provider is connected next.
          setNextPageToken(undefined);
          setHasMoreThreads(true);
          setEmailId(null);
          setEmailCounts({ inbox: 0, important: 0, sent: 0, starred: 0 });
        }
      })
      .catch((error) => {
        console.error("Error disconnecting account:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <div className="emailWrapper">
        {loading ? (
          <div className="spinnerContainer">
            <SimpleSpinner />
          </div>
        ) : loadError ? (
          <EmptyState
            heading="Unable to Load Email Settings"
            bodytext="We couldn't check your connected email account. Please check your connection and try again."
            buttonLabel="Retry"
            onButtonClick={loadProviderStatus}
            isBorder={false}
          />
        ) : providerType === EmailProviderType.None ? (
          <AddGmailAccount border={true} onConnected={loadProviderStatus} allowSmtp allowOutlook />
        ) : (
          <>
            <EmailListingTopHeader
              PageHeading="Emails"
              ButtonName={hasWritePermission ? "Compose Email" : undefined}
              handleSelectChange={handleSelectChange}
              searchTerm={searchEmail}
              setSearchTerm={setSearchEmail}
              filterOptions={filterOptions}
              setActiveTab={setActiveIndex}
              activeTab={activeIndex}
              disabled={compose}
              aggregateValuesFilters={readUnreadFilters}
              onDisconnect={handleDisconnect}
              connectedEmail={connectedEmail}
              onClick={() => {
                setEmailId(null);
                setcompose(true);
              }}
              hasWritePermission={hasWritePermission}
              hideSecondaryFilter={isSmtpConnected || isOutlookConnected}
            />
            <div className="emailSubContainer">
              <div className="emailLeftSection">
                <Inbox
                  threadlist={displayedThreadlist}
                  emaillist={emaillist}
                  setEmailId={setEmailId}
                  emailId={emailId}
                  setcompose={setcompose}
                  listLoading={listLoading}
                  hasMoreThreads={isGmailConnected || isOutlookConnected ? hasMoreThreads : false}
                  onLoadMore={loadMoreEmailThreads}
                />
              </div>
              <div className="emailRightSection">
                {emailId ? (
                  isSmtpConnected ? (
                    <ShowInboxMessage uid={emailId} />
                  ) : isOutlookConnected ? (
                    <ShowOutlookMessage
                      uid={emailId}
                      onGetEmailList={onGetOutlookMessages}
                      setEmailId={setEmailId}
                    />
                  ) : (
                    <ShowEmail
                      emailId={emailId}
                      onGetEmailList={onGetEmailList}
                      setEmailId={setEmailId}
                    />
                  )
                ) : compose ? (
                  <Compose
                    compose={compose}
                    setcompose={setcompose}
                    forwardBody={undefined}
                    getEmailMessages={isSmtpConnected ? onGetInboxMessages : isOutlookConnected ? onGetOutlookMessages : undefined}
                    replyId={undefined}
                    emailId={undefined}
                    setReplyId={undefined}
                    replytype={undefined}
                    providerType={
                      isSmtpConnected
                        ? EmailProviderType.CustomPopSmtp
                        : isOutlookConnected
                        ? EmailProviderType.Office365
                        : EmailProviderType.Gmail
                    }
                  />
                ) : (
                  <EmptyState
                    icon={GetImages.EmptyEmail}
                    heading={"Select An Item To Read"}
                    imgBackgroundClass={Background.LightBlue.Round.round80}
                    isButton={false}
                    isBorder={false}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Emailview;
